/**
 * @file        src/lib/pricingNotes.jsx
 * @description The one-line "how this actually reaches the customer's bill"
 *              notes shown at the top of every money-configuring admin screen
 *              (markup, convenience fee, discount, coupon).
 *
 *              Why they are centralised rather than typed into each page: these
 *              sentences state the ORDER OF OPERATIONS in the two checkout
 *              controllers, and the flight and hotel orders are genuinely
 *              DIFFERENT. Copy-pasted per page, the two would drift and the
 *              console would quietly teach staff the wrong rule.
 *
 *              The authoritative code, which these sentences describe:
 *
 *              FLIGHT — booking_backend/controllers/v1/easebuzzFlightController
 *                subtotal      = ticket (fare + taxes + markup) + add-ons
 *                convenience   = fee rule applied to `subtotal`
 *                DISCOUNTABLE  = subtotal + convenience fee   <-- fee INCLUDED
 *                charged       = discountable - discount - coupon + insurance
 *
 *              HOTEL — booking_backend/controllers/v1/easebuzzHotelController
 *                room total    = FP (supplier net + markup, incl. taxes)
 *                convenience   = fee rule applied to `room total`
 *                                (Fix: amount × rooms × nights; Percent: on the total)
 *                DISCOUNTABLE  = room total                   <-- fee EXCLUDED
 *                charged       = discountable - discount - coupon + convenience
 *
 *              So a flight discount also comes off the convenience fee and a
 *              hotel discount never does. That asymmetry is exactly the kind of
 *              thing an admin cannot infer from a list of rules, which is why
 *              these notes spell out the base instead of just the selection rule.
 * @author      Kinetika Labs
 * @created     2026-09-15
 */

// The discountable base, named the way the checkout actually builds it. Reused
// verbatim in the discount and coupon notes so the two can never disagree about
// what "the total" means on a given product.
export const FLIGHT_BASE = <>Fare + Taxes + Markup + Add-ons (seat/baggage/meal) + Convenience&nbsp;fee</>;
export const HOTEL_BASE = <>Room + Taxes + Markup</>;

/* ---------------- Discounts (automatic, no code) ---------------- */

export const FLIGHT_DISCOUNT_NOTE = (
  <span>
    Applied <b>automatically, with no code</b>: the single best-value active rule
    matching the booking&apos;s brand, buyer type and airline comes off{' '}
    <b>{FLIGHT_BASE}</b>. It <b>stacks</b> with a customer coupon, and the two together
    can never exceed that total.
  </span>
);

export const HOTEL_DISCOUNT_NOTE = (
  <span>
    Applied <b>automatically, with no code</b>: the single best-value active rule
    matching the booking&apos;s brand and buyer type comes off the room total{' '}
    (<b>{HOTEL_BASE}</b>). It <b>stacks</b> with a customer coupon, and the two together
    can never exceed that total. The convenience fee is added <b>after</b> and is never
    discounted.
  </span>
);

/* ---------------- Coupons (customer types a code) ---------------- */

export const FLIGHT_COUPON_NOTE = (
  <span>
    Applied <b>only when the customer enters the code</b> at checkout, on the same
    total the automatic discount uses — <b>{FLIGHT_BASE}</b> — and it <b>stacks on top</b> of
    that discount (the coupon is trimmed first if the two would exceed the total).
    One use is burned per confirmed booking; the insurance premium is never discounted.
  </span>
);

export const HOTEL_COUPON_NOTE = (
  <span>
    Applied <b>only when the customer enters the code</b> at checkout, on the room total{' '}
    (<b>{HOTEL_BASE}</b>) — the same base as the automatic discount, and it <b>stacks on
    top</b> of it (the coupon is trimmed first if the two would exceed the total). One use
    is burned per confirmed booking. The convenience fee is added <b>after</b> and is never
    discounted.
  </span>
);

/* ---------------- Convenience fee (added, not subtracted) ---------------- */
// Appended to the existing "which rule wins" sentence on each fee screen, so one
// banner answers both "which rule?" and "on what, and in what order?".

export const FLIGHT_FEE_BASE_NOTE = (
  <>
    {' '}It is charged on <b>Fare + Taxes + Markup + Add-ons</b>, before any discount — and
    because it sits <b>inside</b> the discountable total, a discount or coupon also comes
    off the fee.
  </>
);

export const HOTEL_FEE_BASE_NOTE = (
  <>
    {' '}It is charged on the room total (<b>{HOTEL_BASE}</b>) and added <b>after</b> the
    discount, so — unlike flights — a discount or coupon never comes off the fee.
    {' '}A <b>Fix</b> amount is charged <b>per room, per night</b>, like the hotel
    markup: ₹350 on 2 rooms for 2 nights is ₹1,400. A <b>Percent</b> amount is taken
    on the whole-stay total, so it already covers every room and night.
  </>
);

/* ---------------- Markup (baked into the displayed price) ---------------- */

export const MARKUP_BASE_NOTE = (
  <>
    {' '}The markup is <b>already inside</b> the price the customer sees, so the convenience
    fee, discounts and coupons are all calculated on the <b>marked-up</b> total — never on
    the supplier net.
  </>
);

/* ---------------- Offers (a marketing card, not a discount) ---------------- */
// An `offers` row carries a plain `coupon_code` STRING. It grants nothing on its
// own — booking_backend/controllers/v1/offerController cross-checks that string
// against the live coupon engine on every read, and withholds the whole card when
// the code is missing, expired, exhausted, or scoped to another brand/audience.
// So "I made an offer but the code doesn't work" is always a Coupon List problem.

export const OFFER_NOTE = (
  <span>
    An offer is the <b>marketing card</b> shown on the storefront — it grants no
    discount by itself. The money comes from the coupon of the same code in the{' '}
    <b>Coupon List</b>: if no live coupon matches it for that brand and buyer type,
    the offer is <b>hidden from the storefront entirely</b> rather than shown with a
    code that would fail at checkout.
  </span>
);

/* ---------------- Wallet / credit line (how a booking gets paid) ---------------- */
// The waterfall in booking_backend/utils/checkoutWallet.js -> placeCheckoutHold:
//   wallet hold (FIFO, oldest-expiring lot first) -> B2B credit line -> gateway.
// Only the wallet reserves money at initiate; the credit line is checked for
// headroom and the due row is written at CAPTURE (creditLineService.captureDue).

export const WALLET_NOTE = (
  <span>
    A company has <b>one shared pool</b> every employee books against — there are no
    per-employee wallets or spend limits. At checkout the total is taken in this order:{' '}
    <b>wallet balance</b> (oldest-expiring money first) → <b>credit line</b> →{' '}
    <b>payment gateway</b>. A company with the wallet enabled spends the pool on
    <b> every</b> booking, whatever the storefront&apos;s &ldquo;Use Wallet&rdquo; tick says.
  </span>
);

// 2026-10-06: the separate credit line is withdrawn (WALLET_DUES_SPEC.md).
// Credit is money IN the wallet — an admin grant carrying an expiry — and the
// part of it that is SPENT becomes a due. Exported under the old name too, so a
// stale import cannot break the build mid-deploy.
export const DUES_NOTE = (
  <span>
    A due is <b>granted money that has been spent</b>. An admin grant with an{' '}
    <b>expiry date</b> is credit; one with no expiry is the customer&apos;s own money and
    never creates a due. The customer&apos;s <b>own money is always spent first</b>, the due
    is raised when a booking is <b>confirmed (captured)</b> — never while it is merely
    held — and a grant that <b>lapses unspent owes nothing</b>. Anything paid in
    afterwards (a top-up, a refund, or a payment recorded here) clears the due before it
    becomes spendable again. There is <b>no due date</b>: age is the signal.
  </span>
);

export const CREDIT_DUES_NOTE = DUES_NOTE;

/* ---------------- Tax Management (the statutory issuer) ---------------- */
// Until 2026-09-15 this screen wrote `tax_company_settings`, a table nothing
// read, so a saved GSTIN never reached an invoice. Migration 056 moved these
// columns onto `site_settings` — the issuer row the documents already read — and
// this note now states the split that keeps two screens off each other's fields.

export const TAX_SETTINGS_NOTE = (
  <span>
    These are the <b>statutory</b> details printed on every tax invoice — GSTIN, PAN,
    registration number, HSN/SAC and your own invoice terms. The company&apos;s{' '}
    <b>name, address, email and phone</b> are not here: they live in{' '}
    <b>Site Setting → company identity</b>, and both screens write the one issuer
    record. A blank GSTIN is simply omitted from the invoice, never printed empty.
  </span>
);
